<template>
  <div class="py-3">
    <div class="step-container d-flex align-items-center position-relative">
      <div
        class="step-line-bg"
        :style="{ left: lineOffset, right: lineOffset }"
      ></div>

      <div
        class="step-line-active"
        :style="{ left: lineOffset, right: progressRight }"
      ></div>

      <!-- steps -->
      <div
        v-for="step in steps"
        :key="step.key"
        class="step-item d-flex flex-column align-items-center text-center flex-fill"
      >
        <!-- circle -->
        <div
          class="step-circle d-flex align-items-center justify-content-center"
          :class="getCircleClass(step.key)"
        >
          <i :class="step.icon"></i>
        </div>

        <!-- label -->
        <div class="mt-2">
          <div
            class="step-label fw-semibold"
            :class="[
              getTextClass(step.key),
              { 'active-label': isActive(step.key) },
            ]"
          >
            {{ step.label }}
          </div>
          <div class="step-desc text-muted">
            {{ step.desc }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { computed } from "vue";

const DEFAULT_STEPS = [
  { key: "DRAFT", label: "Draft", desc: "Aktif", icon: "fa fa-pen", color: "warning" },
  {
    key: "PENDING",
    label: "Pending",
    desc: "Menunggu",
    icon: "fa fa-clock",
    color: "info",
  },
  {
    key: "REVISI",
    label: "Revisi",
    desc: "Perubahan",
    icon: "fa fa-redo",
    color: "danger",
  },
  {
    key: "KOREKSI",
    label: "Koreksi",
    desc: "Perbaikan",
    icon: "fa fa-edit",
    color: "warning",
  },
  {
    key: "APPROVED",
    label: "Approved",
    desc: "Selesai",
    icon: "fa fa-check",
    color: "success",
  },
];

const props = defineProps({
  currentStatus: {
    type: String,
    default: "DRAFT",
  },
  steps: {
    type: Array,
    default: null,
  },
});

const steps = computed(() => props.steps ?? DEFAULT_STEPS);

// helper
const getIndex = (status) => steps.value.findIndex((s) => s.key === status);

const isActive = (key) => {
  return getIndex(key) <= getIndex(props.currentStatus);
};

const getColor = (key) => steps.value.find((s) => s.key === key)?.color;

// warna circle
const getCircleClass = (key) => {
  if (!isActive(key)) return "bg-light border text-muted";

  const color = getColor(key);
  return color ? `bg-${color} text-white` : "bg-light border text-muted";
};

// warna text
const getTextClass = (key) => {
  if (!isActive(key)) return "text-muted";

  const color = getColor(key);
  return color ? `text-${color}` : "text-muted";
};

// progress bar width
const progressRight = computed(() => {
  const currentIndex = getIndex(props.currentStatus);
  // const total = steps.length - 1

  if (currentIndex < 0) return "100%";

  const stepWidth = 100 / steps.value.length;
  const right = 100 - (stepWidth / 2 + currentIndex * stepWidth);

  return `${right}%`;
});

const lineOffset = computed(() => {
  const stepWidth = 100 / steps.value.length;
  return `${stepWidth / 2}%`;
});
</script>

<style scoped>
.step-container {
  width: 100%;
}

/* garis background */
.step-line-bg,
.step-line-active {
  position: absolute;
  top: 16px;
  left: 10%; /* offset kiri */
  right: 10%; /* offset kanan */
  height: 3px;
  z-index: 1;
}

.step-line-bg {
  background-color: #dee2e6;
}

/* garis aktif */
.step-line-active {
  background-color: #0d6efd;
  z-index: 2;
  transition: width 0.4s ease;
}

/* step item */
.step-item {
  min-width: 90px;
  position: relative;
  z-index: 3;
}

/* circle */
.step-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* label */
.step-label {
  font-size: 0.9rem; /* sebelumnya ~0.75rem */
  letter-spacing: 0.3px; /* biar lebih rapi */
}

.active-label {
  font-weight: 600;
  transform: scale(1.05);
}

.step-desc {
  font-size: 0.75rem;
  opacity: 0.8;
}
</style>
